import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Input } from 'src/@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/@/components/ui/select";
import { Checkbox } from 'src/@/components/ui/checkbox';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { getListaUsuariosByClienteId, getListaSetoresTransferencia } from 'src/redux/slices/user';
import { getListaTags } from 'src/redux/slices/chat';
import { Plus, X, Trash2, Upload, FileText, Image, File } from 'lucide-react';
import v2_actionCrmPesquisarLeads from 'src/actions/v2/crm/v2_actionCrmPesquisarLeads';
import actionPesquisarChamados from 'src/actions/chamados/actionPesquisarChamados';
import tarefasService from 'src/services/tarefasService';
import { TD } from './TarefaDialog.design';
import { TAREFAS_DESIGN, PRIORITY_ORDER, getCorAvatar, getIniciais } from './Tarefas.design';

// "Avisar antes": minutos antes do prazo. Precisa bater com REMINDER_OFFSETS do backend.
const REMINDER_OPTIONS = [
  { value: 'none', label: 'Não avisar antes' },
  { value: '15', label: '15 minutos antes' },
  { value: '60', label: '1 hora antes' },
  { value: '180', label: '3 horas antes' },
  { value: '1440', label: '1 dia antes' },
  { value: '4320', label: '3 dias antes' },
];

const FILE_ICON_MAP = {
  'application/pdf': { icon: FileText, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-500/10' },
  'image/': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-500/10' },
  'default': { icon: File, color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-500/10' },
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toDatetimeLocalValue(raw) {
  if (!raw) return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return raw;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T00:00`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const dateOnly = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
  if (dateOnly && typeof raw === 'string') {
    return `${raw.slice(0, 10)}T00:00`;
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
}

// Atalhos de prazo: a maioria das tarefas é "hoje" ou "amanhã". Hoje = 18h (ou a
// próxima hora cheia, se 18h já passou); Amanhã e Próxima semana = 9h.
const QUICK_DUE = [
  { kind: 'hoje', label: 'Hoje' },
  { kind: 'amanha', label: 'Amanhã' },
  { kind: 'semana', label: 'Próxima semana' },
];

function quickDueValue(kind) {
  const d = new Date();
  if (kind === 'hoje') {
    d.setHours(18, 0, 0, 0);
    if (d.getTime() < Date.now()) {
      d.setTime(Date.now());
      d.setMinutes(0, 0, 0);
      d.setHours(d.getHours() + 1);
    }
  } else if (kind === 'amanha') {
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
  } else {
    const add = ((8 - d.getDay()) % 7) || 7; // próxima segunda-feira
    d.setDate(d.getDate() + add);
    d.setHours(9, 0, 0, 0);
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function formatDueDateTimeLabel(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

function getFileIcon(mimetype) {
  if (mimetype === 'application/pdf') return FILE_ICON_MAP['application/pdf'];
  if (mimetype?.startsWith('image/')) return FILE_ICON_MAP['image/'];
  return FILE_ICON_MAP['default'];
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// Rótulo simples, em caixa normal e sem tooltip: o campo se explica pelo nome e,
// quando um comportamento precisa de aviso, a dica vai como texto abaixo dele.
function FieldLabel({ children, small, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className={small ? TD.labelSm : TD.label}>
      {children}
    </label>
  );
}

export default function TarefaDialog({ open, onClose, onSave, task, onDelete, workflows = [], selectedWorkflowId = '', chatId = null, leadId = null, lead = null, lockLead = false, defaultDueDate = null, onExplicarAlertas = null }) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { usuariosList, listaSetoresTransferencia } = useSelector((state) => state.user);
  const { listaTags } = useSelector((state) => state.chat);

  const [form, setForm] = useState({
    title: '',
    description: '',
    status: '',
    priority: 'medium',
    assigned_to: '',
    sector_id: '',
    due_date: '',
    workflow_id: '',
    tags: [],
    checklist: [],
    lead_id: null,
    chamado_id: null,
    chat_id: null,
    attachments: [],
    participants: [],
    reminder_offset_minutes: null,
  });
  // MultiSelect e nao-controlado (defaultValue so semeia o estado interno):
  // incrementa a cada carga de tarefa para remontar o campo com os valores certos.
  const [formVersion, setFormVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);

  // File upload
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const descRef = useRef(null);

  // Lead search
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const leadDebounceRef = useRef(null);
  const leadDropdownRef = useRef(null);

  // Chamado search
  const [chamadoSearch, setChamadoSearch] = useState('');
  const [chamadoResults, setChamadoResults] = useState([]);
  const [chamadoLoading, setChamadoLoading] = useState(false);
  const [showChamadoDropdown, setShowChamadoDropdown] = useState(false);
  const [selectedChamado, setSelectedChamado] = useState(null);
  const chamadoDebounceRef = useRef(null);
  const chamadoDropdownRef = useRef(null);

  useEffect(() => {
    if (user?.clienteId) {
      if (usuariosList.length === 0) dispatch(getListaUsuariosByClienteId(user.clienteId));
      if (listaSetoresTransferencia.length === 0) dispatch(getListaSetoresTransferencia(user.clienteId));
      if (listaTags.length === 0) dispatch(getListaTags(`?clienteId=${user.clienteId}`));
    }
  }, [user?.clienteId]);

  const currentWorkflow = useMemo(() => {
    return workflows.find(w => w._id === form.workflow_id);
  }, [workflows, form.workflow_id]);

  const sortedEstagios = useMemo(() => {
    if (!currentWorkflow?.estagios) return [];
    return [...currentWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
  }, [currentWorkflow]);

  const filteredUsers = useMemo(() => {
    if (!form.sector_id) return usuariosList;
    return usuariosList.filter(u =>
      u.setores?.some(s => (s._id || s) === form.sector_id) || !u.setores?.length
    );
  }, [usuariosList, form.sector_id]);

  // Criador e responsavel ja sao destinatarios dos alertas (o backend inclusive
  // remove o criador da lista ao salvar). Tira-los das opcoes evita o
  // "selecionei, salvei, reabri e sumiu".
  // Quem receberá alerta desta tarefa, com os nomes reais. Mesma regra do
  // backend (getDestinatariosTarefa): criador + responsável + participantes,
  // sem repetir quem acumula papéis.
  const destinatariosAlerta = useMemo(() => {
    const nomeDe = (u) => u?.nomeFantasia || u?.nome || u?.email || '';
    const acharUsuario = (id) => usuariosList.find(u => u._id === id);

    const criadorId = task?.created_by?._id || task?.created_by || user?.id;
    const lista = [];
    const vistos = new Set();

    const push = (id, nome, papel) => {
      const chave = id ? String(id) : `nome:${nome}`;
      if (!nome || vistos.has(chave)) return;
      vistos.add(chave);
      lista.push({ id: chave, nome, papel });
    };

    if (task?.created_by) {
      const ehVoce = String(criadorId) === String(user?.id);
      push(criadorId, ehVoce ? 'Você' : nomeDe(task.created_by) || nomeDe(acharUsuario(criadorId)), 'criador');
    } else {
      push(user?.id, 'Você', 'criador');
    }

    if (form.assigned_to) push(form.assigned_to, nomeDe(acharUsuario(form.assigned_to)), 'responsável');
    form.participants.forEach(id => push(id, nomeDe(acharUsuario(id)), 'participante'));

    return lista;
  }, [task, user?.id, usuariosList, form.assigned_to, form.participants]);

  // O que dispara aviso, adaptado ao que já está preenchido.
  const resumoAvisos = useMemo(() => {
    const partes = [task ? 'mudança de responsável' : 'criação da tarefa', 'comentários', 'conclusão'];
    if (form.due_date) {
      const opt = REMINDER_OPTIONS.find(o => o.value === String(form.reminder_offset_minutes));
      partes.push(opt && form.reminder_offset_minutes != null
        ? `lembrete ${opt.label.toLowerCase()} e no vencimento`
        : 'vencimento do prazo');
    }
    return `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;
  }, [task, form.due_date, form.reminder_offset_minutes]);

  const participantOptions = useMemo(() => {
    const creatorId = task?.created_by?._id || task?.created_by || user?.id;
    return usuariosList
      .filter(u => u._id !== form.assigned_to && u._id !== creatorId)
      .map(u => ({ value: u._id, label: u.nomeFantasia || u.nome || u.email }));
  }, [usuariosList, form.assigned_to, task, user?.id]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const applyTaskToForm = (source) => {
      const taskWorkflowId = source.workflow_id?._id || source.workflow_id || selectedWorkflowId;
      setForm({
        title: source.title || '',
        description: source.description || '',
        status: source.status || '',
        priority: source.priority || 'medium',
        assigned_to: source.assigned_to?._id || source.assigned_to || '',
        sector_id: source.sector_id?._id || source.sector_id || '',
        due_date: toDatetimeLocalValue(source.due_date),
        workflow_id: taskWorkflowId,
        tags: source.tags || [],
        checklist: source.checklist || [],
        lead_id: source.lead_id?._id || source.lead_id || leadId || null,
        chamado_id: source.chamado_id?._id || source.chamado_id || null,
        chat_id: source.chat_id?._id || source.chat_id || chatId || null,
        attachments: source.attachments || [],
        participants: (source.participants || []).map(p => p?._id || p).filter(Boolean),
        reminder_offset_minutes: source.reminder_offset_minutes ?? null,
      });
      setFormVersion(v => v + 1);
      if (source.lead_id && typeof source.lead_id === 'object') {
        setSelectedLead(source.lead_id);
      } else if (lead) {
        setSelectedLead(lead);
      } else {
        setSelectedLead(null);
      }
      if (source.chamado_id && typeof source.chamado_id === 'object') {
        setSelectedChamado(source.chamado_id);
      } else {
        setSelectedChamado(null);
      }
    };

    setPendingFiles([]);
    setUploading(false);
    setShowTagInput(false);
    setNewTag('');
    setNewChecklistItem('');
    setLeadSearch('');
    setLeadResults([]);
    setShowLeadDropdown(false);
    setChamadoSearch('');
    setChamadoResults([]);
    setShowChamadoDropdown(false);

    if (task) {
      applyTaskToForm(task);
      (async () => {
        try {
          const response = await tarefasService.buscar(task._id);
          if (cancelled || response.error || !response.data) return;
          applyTaskToForm(response.data);
        } catch (err) {
          console.error('Erro ao carregar detalhes da tarefa:', err);
        }
      })();
    } else {
      const defaultWorkflowId = selectedWorkflowId;
      const defaultWorkflow = workflows.find(w => w._id === defaultWorkflowId);
      const firstStatus = defaultWorkflow?.estagios?.length > 0
        ? [...defaultWorkflow.estagios].sort((a, b) => a.ordem - b.ordem)[0].nome
        : 'A Fazer';
      setForm({
        title: '',
        description: '',
        status: firstStatus,
        priority: 'medium',
        assigned_to: '',
        sector_id: '',
        due_date: toDatetimeLocalValue(defaultDueDate),
        workflow_id: defaultWorkflowId,
        tags: [],
        checklist: [],
        lead_id: leadId || null,
        chamado_id: null,
        chat_id: chatId || null,
        attachments: [],
        participants: [],
        reminder_offset_minutes: null,
      });
      setFormVersion(v => v + 1);
      setSelectedLead(lead || null);
      setSelectedChamado(null);
    }

    return () => { cancelled = true; };
  }, [task, open, selectedWorkflowId, workflows, leadId, chatId, defaultDueDate]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (leadDropdownRef.current && !leadDropdownRef.current.contains(e.target)) {
        setShowLeadDropdown(false);
      }
      if (chamadoDropdownRef.current && !chamadoDropdownRef.current.contains(e.target)) {
        setShowChamadoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Drawer: Esc fecha (menos quando um Select/Popover do Radix está aberto — o
  // popper ainda no DOM é o sinal) e a página atrás não rola enquanto aberto.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Descrição sem caixa: cresce com o texto até um teto e, passado o teto, rola
  // por dentro — é o único elemento elástico da coluna, que assim não rola.
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    const CAP = 160;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, CAP)}px`;
    el.style.overflowY = el.scrollHeight > CAP ? 'auto' : 'hidden';
  }, [form.description, open]);

  const handleWorkflowChange = (newWorkflowId) => {
    const wf = workflows.find(w => w._id === newWorkflowId);
    const firstStatus = wf?.estagios?.length > 0
      ? [...wf.estagios].sort((a, b) => a.ordem - b.ordem)[0].nome
      : '';
    setForm({ ...form, workflow_id: newWorkflowId, status: firstStatus });
  };

  // Lead search with debounce
  const searchLeads = useCallback((termo) => {
    if (leadDebounceRef.current) clearTimeout(leadDebounceRef.current);
    if (!termo || termo.length < 2) {
      setLeadResults([]);
      setShowLeadDropdown(false);
      return;
    }
    setLeadLoading(true);
    leadDebounceRef.current = setTimeout(async () => {
      try {
        const res = await v2_actionCrmPesquisarLeads(`?cliente_id=${user?.clienteId}&termo_pesquisa=${encodeURIComponent(termo)}&limite=10`);
        setLeadResults(res.data?.data || res.data || []);
        setShowLeadDropdown(true);
      } catch (err) {
        console.error('Erro ao pesquisar leads:', err);
        setLeadResults([]);
      } finally {
        setLeadLoading(false);
      }
    }, 300);
  }, [user?.clienteId]);

  const handleLeadSearchChange = (e) => {
    const value = e.target.value;
    setLeadSearch(value);
    searchLeads(value);
  };

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setForm({ ...form, lead_id: lead._id });
    setLeadSearch('');
    setLeadResults([]);
    setShowLeadDropdown(false);
  };

  const handleRemoveLead = () => {
    setSelectedLead(null);
    setForm({ ...form, lead_id: null });
  };

  // Chamado search with debounce
  const searchChamados = useCallback((termo) => {
    if (chamadoDebounceRef.current) clearTimeout(chamadoDebounceRef.current);
    if (!termo || termo.length < 2) {
      setChamadoResults([]);
      setShowChamadoDropdown(false);
      return;
    }
    setChamadoLoading(true);
    chamadoDebounceRef.current = setTimeout(async () => {
      try {
        const res = await actionPesquisarChamados({ cliente_id: user?.clienteId, search: termo, limit: 10 });
        setChamadoResults(res.data?.data || res.data || []);
        setShowChamadoDropdown(true);
      } catch (err) {
        console.error('Erro ao pesquisar chamados:', err);
        setChamadoResults([]);
      } finally {
        setChamadoLoading(false);
      }
    }, 300);
  }, [user?.clienteId]);

  const handleChamadoSearchChange = (e) => {
    const value = e.target.value;
    setChamadoSearch(value);
    searchChamados(value);
  };

  const handleSelectChamado = (chamado) => {
    setSelectedChamado(chamado);
    setForm({ ...form, chamado_id: chamado._id });
    setChamadoSearch('');
    setChamadoResults([]);
    setShowChamadoDropdown(false);
  };

  const handleRemoveChamado = () => {
    setSelectedChamado(null);
    setForm({ ...form, chamado_id: null });
  };

  // Tags
  const handleAddTag = (tagName) => {
    if (!tagName.trim()) return;
    if (form.tags.includes(tagName.trim())) return;
    setForm({ ...form, tags: [...form.tags, tagName.trim()] });
    setNewTag('');
    setShowTagInput(false);
  };

  const handleRemoveTag = (tagToRemove) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tagToRemove) });
  };

  // Checklist
  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    const newItem = {
      id: Date.now().toString(),
      text: newChecklistItem.trim(),
      checked: false,
    };
    setForm({ ...form, checklist: [...form.checklist, newItem] });
    setNewChecklistItem('');
  };

  const handleToggleChecklistItem = (itemId) => {
    setForm({
      ...form,
      checklist: form.checklist.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      ),
    });
  };

  const handleRemoveChecklistItem = (itemId) => {
    setForm({ ...form, checklist: form.checklist.filter(item => item.id !== itemId) });
  };

  const handleChecklistKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddChecklistItem();
    }
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    processFiles(files);
  };

  const processFiles = async (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(f => f.size <= maxSize);

    if (task) {
      // Edit mode: upload immediately
      setUploading(true);
      for (const file of validFiles) {
        try {
          const response = await tarefasService.uploadArquivo(task._id, file);
          if (!response.error && response.data) {
            setForm(prev => ({ ...prev, attachments: [...prev.attachments, response.data] }));
          }
        } catch (err) {
          console.error('Erro ao enviar arquivo:', err);
        }
      }
      setUploading(false);
    } else {
      // Create mode: queue files
      setPendingFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleRemoveAttachment = async (attachment) => {
    if (task && attachment._id) {
      try {
        await tarefasService.removerArquivo(task._id, attachment._id);
        setForm(prev => ({
          ...prev,
          attachments: prev.attachments.filter(a => a._id !== attachment._id)
        }));
      } catch (err) {
        console.error('Erro ao remover arquivo:', err);
      }
    }
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.status || !form.priority) return;
    setSaving(true);
    try {
      const data = { ...form };
      delete data.attachments; // Don't send attachments in the form data
      if (!data.assigned_to) delete data.assigned_to;
      if (!data.sector_id) delete data.sector_id;
      if (!data.due_date) {
        // Em edicao manda null para conseguir LIMPAR o prazo (antes a chave era
        // apagada e o prazo antigo ficava). Em criacao basta omitir.
        if (task) data.due_date = null; else delete data.due_date;
        data.reminder_offset_minutes = null;
      } else {
        data.due_date = fromDatetimeLocalValue(data.due_date);
      }
      // participants vai sempre (mesmo vazio) para que remocoes persistam.
      data.participants = Array.isArray(data.participants) ? data.participants : [];
      if (!data.lead_id) data.lead_id = null;
      if (!data.chamado_id) data.chamado_id = null;
      if (!data.chat_id) delete data.chat_id;
      data.userId = user?.id;
      const savedTask = await onSave(data);

      // Upload pending files for new tasks
      if (!task && savedTask?._id && pendingFiles.length > 0) {
        setUploading(true);
        for (const file of pendingFiles) {
          try {
            await tarefasService.uploadArquivo(savedTask._id, file);
          } catch (err) {
            console.error('Erro ao enviar arquivo pendente:', err);
          }
        }
        setPendingFiles([]);
        setUploading(false);
      }

      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete && task) {
      onDelete(task);
      onClose();
    }
  };

  const checklistProgress = form.checklist.length > 0
    ? Math.round((form.checklist.filter(i => i.checked).length / form.checklist.length) * 100)
    : 0;

  const isOverdue = useMemo(() => {
    if (!form.due_date) return false;
    if (form.status === 'Concluído' || form.status === 'done') return false;
    if (currentWorkflow?.estagios?.length > 0) {
      const sorted = [...currentWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
      if (sorted[sorted.length - 1]?.nome === form.status) return false;
    }
    const due = new Date(form.due_date);
    if (Number.isNaN(due.getTime())) return false;
    return due < new Date();
  }, [form.due_date, form.status, currentWorkflow]);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const allAttachments = [...form.attachments, ...pendingFiles.map((f, i) => ({ _pending: true, _index: i, original_name: f.name, mimetype: f.type, size: f.size }))];

  // Guard: don't render creation form if no workflows exist and not editing
  if (open && workflows.length === 0 && !task) return null;

  if (!open) return null;

  const PRIORITY = TAREFAS_DESIGN.priority;
  const isEdit = Boolean(task);
  const createdByName = task?.created_by?.nomeFantasia || task?.created_by?.nome || task?.created_by?.email;
  const createdAtLabel = task?.created_at ? new Date(task.created_at).toLocaleDateString('pt-BR') : '';
  const busy = saving || uploading;
  const canSave = !busy && form.title.trim() && form.status && form.priority;
  const doneCount = form.checklist.filter(i => i.checked).length;
  const leadAtual = selectedLead || (lockLead && lead) || null;

  // Portal para o body: o Dialog antigo fazia isso, e alguns chamadores
  // (DrawerNegocioTarefas, ChatInfoTarefas) vivem dentro de painéis com
  // transform, que viraria o containing block do `fixed`.
  return createPortal(
    <div className={TD.overlay} role="dialog" aria-modal="true" aria-labelledby="tarefa-drawer-title">
      <div className={TD.backdrop} onClick={onClose} />

      <div className={TD.panel}>
        {/* Header */}
        <div className={TD.header}>
          <div className="flex items-center gap-3 min-w-0">
            <div className={`${TD.headerIcon} ${isEdit ? TD.headerIconEdit : TD.headerIconCreate}`}>
              <Iconify icon={isEdit ? 'eva:edit-2-outline' : 'eva:plus-outline'} width={18} />
            </div>
            <div className="min-w-0">
              <h2 id="tarefa-drawer-title" className={TD.title}>{isEdit ? 'Editar tarefa' : 'Nova tarefa'}</h2>
              <p className={TD.subtitle}>
                {isEdit
                  ? (createdByName ? `Criada por ${createdByName}${createdAtLabel ? ` em ${createdAtLabel}` : ''}` : 'Atualize o que mudou.')
                  : 'O que fazer, quem faz e até quando.'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={TD.close} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        {isOverdue && (
          <div className={TD.banner}>
            <Iconify icon="eva:clock-outline" width={16} className="flex-shrink-0" />
            Tarefa atrasada — o prazo era {formatDueDateTimeLabel(form.due_date)}
          </div>
        )}

        <div className={TD.body}>
          {/* ── Esquerda: a tarefa ── */}
          <div className={TD.left}>
            <div className={TD.hero}>
              <input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="O que precisa ser feito?"
                aria-label="Título da tarefa"
                autoFocus={!isEdit}
                className={TD.heroTitle}
              />
              <textarea
                ref={descRef}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Adicione detalhes, contexto ou instruções…"
                aria-label="Descrição da tarefa"
                rows={2}
                className={TD.heroDesc}
              />
            </div>

            <div className={TD.sections}>
              {/* Fluxo */}
              <section className={TD.section}>
                <div className={TD.sectionHead}>
                  <div className={TD.sectionIcon}><Iconify icon="mdi:source-branch" width={16} /></div>
                  <h3 className={TD.sectionTitle}>Fluxo</h3>
                </div>
                <div className={TD.gridFluxo}>
                  {workflows.length > 0 && (
                    <div className={TD.field}>
                      <FieldLabel>Workflow</FieldLabel>
                      <Select value={form.workflow_id || undefined} onValueChange={handleWorkflowChange}>
                        <SelectTrigger className={TD.selectTrigger}><SelectValue placeholder="Selecionar…" /></SelectTrigger>
                        <SelectContent className={TD.selectContent}>
                          {workflows.map(w => <SelectItem key={w._id} value={w._id}>{w.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className={TD.field}>
                    <FieldLabel>Status</FieldLabel>
                    <Select value={form.status || undefined} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger className={TD.selectTrigger}><SelectValue /></SelectTrigger>
                      <SelectContent className={TD.selectContent}>
                        {sortedEstagios.length > 0 ? sortedEstagios.map(e => (
                          <SelectItem key={e.nome} value={e.nome}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.cor }} />
                              {e.nome}
                            </span>
                          </SelectItem>
                        )) : (
                          <>
                            <SelectItem value="A Fazer">A Fazer</SelectItem>
                            <SelectItem value="Em Progresso">Em Progresso</SelectItem>
                            <SelectItem value="Concluído">Concluído</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className={TD.field}>
                    <FieldLabel>Prioridade</FieldLabel>
                    <div className={TD.segment} role="radiogroup" aria-label="Prioridade">
                      {PRIORITY_ORDER.map(p => {
                        const active = form.priority === p;
                        return (
                          <button
                            key={p}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => setForm({ ...form, priority: p })}
                            className={`${TD.segmentItem} ${active ? TD.segmentItemActive : ''}`}
                          >
                            <span className={TD.segmentDot} style={{ backgroundColor: PRIORITY[p].color }} />
                            {PRIORITY[p].label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>

              {/* Pessoas */}
              <section className={TD.section}>
                <div className={TD.sectionHead}>
                  <div className={TD.sectionIcon}><Iconify icon="mdi:account-group-outline" width={16} /></div>
                  <h3 className={TD.sectionTitle}>Pessoas</h3>
                </div>
                <div className={TD.grid3}>
                  <div className={TD.field}>
                    <FieldLabel>Setor</FieldLabel>
                    <Select value={form.sector_id || 'none'} onValueChange={v => setForm({ ...form, sector_id: v === 'none' ? '' : v, assigned_to: '' })}>
                      <SelectTrigger className={TD.selectTrigger}><SelectValue placeholder="Todos os setores" /></SelectTrigger>
                      <SelectContent className={TD.selectContent}>
                        <SelectItem value="none">Todos os setores</SelectItem>
                        {listaSetoresTransferencia.map(s => <SelectItem key={s._id} value={s._id}>{s.nome_setor}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={TD.field}>
                    <FieldLabel>Responsável</FieldLabel>
                    <Select value={form.assigned_to || 'none'} onValueChange={v => setForm({ ...form, assigned_to: v === 'none' ? '' : v })}>
                      <SelectTrigger className={TD.selectTrigger}><SelectValue placeholder="Ninguém ainda" /></SelectTrigger>
                      <SelectContent className={TD.selectContent}>
                        <SelectItem value="none">Ninguém ainda</SelectItem>
                        {filteredUsers.map(u => <SelectItem key={u._id} value={u._id}>{u.nomeFantasia || u.nome || u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={TD.field}>
                    <FieldLabel>Participantes</FieldLabel>
                    <MultiSelect
                      key={`participantes-${formVersion}`}
                      options={participantOptions}
                      defaultValue={form.participants}
                      onValueChange={values => setForm(prev => ({ ...prev, participants: values }))}
                      placeholder="Adicionar…"
                      maxCount={1}
                      modalPopover
                      className={TD.multiTrigger}
                      contentClassName="z-[10000]"
                      badgeClassName="hover:translate-y-0 hover:scale-100 rounded-md"
                    />
                  </div>
                </div>
                {/* Quem será avisado, com nome e papel: a resposta concreta fica
                    à vista enquanto o usuário escolhe responsável/participantes. */}
                <div className={TD.alertBox}>
                  <div className={TD.alertHead}>
                    <Iconify icon="mdi:bell-outline" width={14} className={TD.alertIcon} />
                    <span className={TD.alertLabel}>Serão avisados</span>
                    {destinatariosAlerta.length > 0 ? destinatariosAlerta.map(d => (
                      <span key={d.id} className={TD.alertChip}>
                        <span className={TD.alertAvatar} style={{ backgroundColor: getCorAvatar(d.nome) }}>
                          {getIniciais(d.nome)}
                        </span>
                        <span className={TD.alertName}>{d.nome}</span>
                        <span className={TD.alertRole}>{d.papel}</span>
                      </span>
                    )) : (
                      <span className={TD.alertEmpty}>ninguém ainda — defina um responsável.</span>
                    )}
                  </div>
                  <p className={TD.alertNote}>
                    Avisos em {resumoAvisos}. Quem faz a ação não recebe o próprio aviso.
                    {onExplicarAlertas && (
                      <>
                        {' '}
                        <button type="button" onClick={onExplicarAlertas} className={TD.helpLink}>
                          Como funcionam?
                        </button>
                      </>
                    )}
                  </p>
                </div>
              </section>

              {/* Prazo */}
              <section className={TD.section}>
                <div className={TD.sectionHead}>
                  <div className={TD.sectionIcon}><Iconify icon="mdi:calendar-clock-outline" width={16} /></div>
                  <h3 className={TD.sectionTitle}>Prazo</h3>
                  {form.due_date && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, due_date: '', reminder_offset_minutes: null })}
                      className={TD.sectionAction}
                    >
                      Remover prazo
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {QUICK_DUE.map(q => {
                    const value = quickDueValue(q.kind);
                    const active = form.due_date === value;
                    return (
                      <button
                        key={q.kind}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setForm({ ...form, due_date: value })}
                        className={`${TD.chip} ${active ? TD.chipActive : TD.chipIdle}`}
                      >
                        {q.label}
                      </button>
                    );
                  })}
                  <input
                    type="datetime-local"
                    value={form.due_date}
                    onChange={e => setForm({
                      ...form,
                      due_date: e.target.value,
                      ...(e.target.value ? {} : { reminder_offset_minutes: null }),
                    })}
                    aria-label="Data e hora de conclusão"
                    className={TD.dateInput}
                  />
                </div>
                <div className={TD.reminderRow}>
                  <Iconify
                    icon="mdi:bell-outline"
                    width={16}
                    className={form.due_date ? 'text-[#7C3AED] flex-shrink-0' : 'text-[#d4d4d8] dark:text-[#5a5a5a] flex-shrink-0'}
                  />
                  <span className={TD.reminderLabel}>Avisar antes</span>
                  <Select
                    value={form.reminder_offset_minutes == null ? 'none' : String(form.reminder_offset_minutes)}
                    onValueChange={v => setForm({ ...form, reminder_offset_minutes: v === 'none' ? null : Number(v) })}
                    disabled={!form.due_date}
                  >
                    <SelectTrigger className={TD.reminderTrigger}><SelectValue placeholder="Não avisar antes" /></SelectTrigger>
                    <SelectContent className={TD.selectContent}>
                      {REMINDER_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className={TD.helpInline}>
                    {form.due_date ? 'O aviso no vencimento é sempre enviado.' : 'Defina o prazo para ativar o lembrete.'}
                  </span>
                </div>
              </section>
            </div>
          </div>

          {/* ── Direita: contexto ── */}
          <div className={TD.right}>
            {/* Vínculos */}
            <div className={TD.card}>
              <div className={TD.cardHead}>
                <Iconify icon="mdi:link-variant" width={16} className={TD.cardIcon} />
                <h3 className={TD.cardTitle}>Vínculos</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <FieldLabel small>Lead / negócio</FieldLabel>
                  {leadAtual ? (
                    <div className={TD.linkLead}>
                      <div className={TD.linkIconLead}>
                        <Iconify icon="mdi:handshake-outline" width={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={TD.linkName}>{leadAtual.nome}</p>
                        {leadAtual.valor !== undefined && leadAtual.valor !== null && (
                          <p className={TD.linkMeta}>{formatCurrency(leadAtual.valor)}</p>
                        )}
                      </div>
                      {!lockLead && (
                        <button type="button" onClick={handleRemoveLead} className={TD.linkRemove} aria-label="Remover lead">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className={TD.searchWrap} ref={leadDropdownRef}>
                      <div className="relative">
                        <Input
                          value={leadSearch}
                          onChange={handleLeadSearchChange}
                          placeholder="Buscar lead pelo nome…"
                          className={TD.searchInput}
                        />
                        <Iconify
                          icon={leadLoading ? 'mdi:loading' : 'eva:search-outline'}
                          width={16}
                          className={`${TD.searchIcon} ${leadLoading ? 'animate-spin' : ''}`}
                        />
                      </div>
                      {showLeadDropdown && leadResults.length > 0 && (
                        <div className={TD.dropdown}>
                          {leadResults.map(l => (
                            <button key={l._id} type="button" onClick={() => handleSelectLead(l)} className={TD.dropdownItem}>
                              <div className={TD.linkIconLead}>
                                <Iconify icon="mdi:handshake-outline" width={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={TD.linkName}>{l.nome}</p>
                                {l.valor !== undefined && l.valor !== null && (
                                  <p className={TD.linkMeta}>{formatCurrency(l.valor)}</p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {showLeadDropdown && leadResults.length === 0 && !leadLoading && leadSearch.length >= 2 && (
                        <div className={TD.dropdownEmpty}>Nenhum lead com esse nome.</div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <FieldLabel small>Chamado</FieldLabel>
                  {selectedChamado ? (
                    <div className={TD.linkChamado}>
                      <div className={TD.linkIconChamado}>
                        <Iconify icon="mdi:ticket-outline" width={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={TD.linkName}>
                          {selectedChamado.chamado ? `#${selectedChamado.chamado} — ` : ''}{selectedChamado.titulo}
                        </p>
                        {selectedChamado.status && <p className={TD.linkMeta}>{selectedChamado.status}</p>}
                      </div>
                      <button type="button" onClick={handleRemoveChamado} className={TD.linkRemove} aria-label="Remover chamado">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className={TD.searchWrap} ref={chamadoDropdownRef}>
                      <div className="relative">
                        <Input
                          value={chamadoSearch}
                          onChange={handleChamadoSearchChange}
                          placeholder="Buscar por título ou número…"
                          className={TD.searchInput}
                        />
                        <Iconify
                          icon={chamadoLoading ? 'mdi:loading' : 'eva:search-outline'}
                          width={16}
                          className={`${TD.searchIcon} ${chamadoLoading ? 'animate-spin' : ''}`}
                        />
                      </div>
                      {showChamadoDropdown && chamadoResults.length > 0 && (
                        <div className={TD.dropdown}>
                          {chamadoResults.map(c => (
                            <button key={c._id} type="button" onClick={() => handleSelectChamado(c)} className={TD.dropdownItem}>
                              <div className={TD.linkIconChamado}>
                                <Iconify icon="mdi:ticket-outline" width={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={TD.linkName}>{c.chamado ? `#${c.chamado} — ` : ''}{c.titulo}</p>
                                {c.status && <p className={TD.linkMeta}>{c.status}</p>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {showChamadoDropdown && chamadoResults.length === 0 && !chamadoLoading && chamadoSearch.length >= 2 && (
                        <div className={TD.dropdownEmpty}>Nenhum chamado encontrado.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Checklist */}
            <div className={TD.card}>
              <div className={TD.cardHead}>
                <Iconify icon="mdi:checkbox-marked-outline" width={16} className={TD.cardIcon} />
                <h3 className={TD.cardTitle}>Checklist</h3>
                {form.checklist.length > 0 && (
                  <span className={TD.cardMeta}>{doneCount} de {form.checklist.length}</span>
                )}
              </div>

              {form.checklist.length > 0 && (
                <div className={TD.progress}>
                  <div className={TD.progressBar} style={{ width: `${checklistProgress}%` }} />
                </div>
              )}

              <div className="space-y-1">
                {form.checklist.map((item) => (
                  <div key={item.id} className={TD.checkItem}>
                    <Checkbox
                      checked={item.checked}
                      onCheckedChange={() => handleToggleChecklistItem(item.id)}
                      className={TD.checkbox}
                    />
                    <span className={item.checked ? TD.checkTextDone : TD.checkText}>{item.text}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveChecklistItem(item.id)}
                      className={TD.checkRemove}
                      aria-label="Remover item"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>

              <div className={TD.addRow}>
                <Input
                  value={newChecklistItem}
                  onChange={e => setNewChecklistItem(e.target.value)}
                  onKeyDown={handleChecklistKeyDown}
                  placeholder="Novo item — Enter para adicionar"
                  className={TD.input}
                />
                <button
                  type="button"
                  onClick={handleAddChecklistItem}
                  disabled={!newChecklistItem.trim()}
                  className={TD.addBtn}
                  aria-label="Adicionar item"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Tags */}
            <div className={TD.card}>
              <div className={TD.cardHead}>
                <Iconify icon="mdi:tag-outline" width={16} className={TD.cardIcon} />
                <h3 className={TD.cardTitle}>Tags</h3>
              </div>
              <div className={TD.tagList}>
                {form.tags.map((tag, i) => (
                  <span key={i} className={TD.tag}>
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className={TD.tagRemove} aria-label={`Remover tag ${tag}`}>
                      <X size={10} />
                    </button>
                  </span>
                ))}

                {showTagInput ? (
                  <span className="inline-flex items-center gap-1">
                    <Input
                      value={newTag}
                      onChange={e => setNewTag(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); handleAddTag(newTag); }
                        if (e.key === 'Escape') { e.stopPropagation(); setShowTagInput(false); setNewTag(''); }
                      }}
                      placeholder="Nome da tag"
                      className={TD.tagInput}
                      autoFocus
                    />
                    <button type="button" onClick={() => handleAddTag(newTag)} className={TD.tagConfirm} aria-label="Confirmar tag">
                      <Iconify icon="eva:checkmark-outline" width={14} />
                    </button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setShowTagInput(true)} className={TD.tagAdd}>
                    <Plus size={12} />
                    Adicionar
                  </button>
                )}
              </div>

              {showTagInput && listaTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {listaTags
                    .filter(t => !form.tags.includes(t.nome) && t.nome?.toLowerCase().includes(newTag.toLowerCase()))
                    .slice(0, 8)
                    .map(t => (
                      <button key={t._id} type="button" onClick={() => handleAddTag(t.nome)} className={TD.tagSuggest}>
                        {t.nome}
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Anexos */}
            <div className={TD.card}>
              <div className={TD.cardHead}>
                <Iconify icon="mdi:paperclip" width={16} className={TD.cardIcon} />
                <h3 className={TD.cardTitle}>Anexos</h3>
                {allAttachments.length > 0 && <span className={TD.cardMeta}>{allAttachments.length}</span>}
              </div>
              <div
                className={TD.dropzone}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={handleFileDrop}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
              >
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
                {uploading ? (
                  <Iconify icon="mdi:loading" width={22} className="text-[#7C3AED] mx-auto animate-spin" />
                ) : (
                  <Upload size={22} className="text-[#a1a1aa] mx-auto" />
                )}
                <p className={TD.dropTitle}>Arraste arquivos ou clique para escolher</p>
                <p className={TD.dropMeta}>PDF, imagens e documentos até 10 MB</p>
              </div>

              {allAttachments.length > 0 && (
                <div className={TD.fileList}>
                  {allAttachments.map((attachment, idx) => {
                    const fileInfo = getFileIcon(attachment.mimetype);
                    const IconComp = fileInfo.icon;
                    const isPending = attachment._pending;
                    return (
                      <div
                        key={attachment._id || `pending-${idx}`}
                        className={`${TD.file} ${isPending ? TD.filePending : TD.fileSaved}`}
                      >
                        <div className={`${TD.fileIcon} ${fileInfo.bg}`}>
                          <IconComp size={16} className={fileInfo.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          {!isPending && attachment.url ? (
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${TD.fileName} ${TD.fileNameLink}`}
                            >
                              {attachment.original_name || attachment.filename}
                            </a>
                          ) : (
                            <p className={TD.fileName}>{attachment.original_name || attachment.filename}</p>
                          )}
                          <div className="flex items-center gap-2">
                            <span className={TD.fileSize}>{formatFileSize(attachment.size)}</span>
                            {isPending && <span className={TD.filePendingTag}>Envia ao salvar</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => isPending ? handleRemovePendingFile(attachment._index) : handleRemoveAttachment(attachment)}
                          className={TD.fileRemove}
                          aria-label="Remover anexo"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={TD.footer}>
          <div>
            {isEdit && onDelete && (
              <button type="button" onClick={handleDeleteClick} className={TD.deleteBtn}>
                <Trash2 size={14} />
                Excluir tarefa
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className={TD.cancelBtn}>Cancelar</button>
            <button type="button" onClick={handleSave} disabled={!canSave} className={TD.primaryBtn}>
              {busy && <Iconify icon="mdi:loading" className="animate-spin" width={16} />}
              {busy ? 'Salvando…' : (isEdit ? 'Salvar alterações' : 'Criar tarefa')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
